import { createSlice, createAsyncThunk, createEntityAdapter, current } from "@reduxjs/toolkit";
import { envAwarePost, envAwareFormSubmit } from "../../util/util";

export const removeMuscleGroups = createAsyncThunk(
  "removeMuscleGroups",
  async muscle_ids => await envAwarePost(
    "api/exercises/api/",
    {action: "removeMuscleGroups",muscle_ids}
  )
);

export const getExerciseList = createAsyncThunk(
  "getExerciseList",
  async (payload) => envAwarePost("api/events/api/", {
    action: "getExerciseList",
    ...payload
  })
);

export const getLibraryExercises = createAsyncThunk(
  "getLibraryExercises",
  async (payload) => envAwarePost("api/exercises/api/", {
    action: "getLibraryExercises",
    ...payload
  })
);

export const getLibraryExercisesAfterAdding = createAsyncThunk( // this is to update the list without causing table to dissappear
  "getLibraryExercisesAfterAdding",
  async (payload) => envAwarePost("api/exercises/api/", {
    action: "getLibraryExercises",
    ...payload
  })
);

export const getAdditionalExerciseData = createAsyncThunk(
  "getAdditionalExerciseData",
  async () => envAwarePost("api/events/api/", {
    action: "getAdditionalExerciseData",
  })
);

export const getMuscleGroups = createAsyncThunk(
  "getMuscleGroups",
  async (payload) => envAwarePost("api/events/api/", {
    action: "getMuscleGroups",
    user_id:payload.user_id
  })
);

export const addMuscleGroup = createAsyncThunk(
  "addMuscleGroup",
  async (payload) => await envAwarePost("api/events/api/", {
    action: "addMuscleGroup",
    item_db_name: payload.name,
    user_id:payload.user_id
  }
  )
);

export const editMuscleGroup = createAsyncThunk(
  "editMuscleGroup",
  async (payload) => await envAwarePost("api/events/api/", {
    action: "editMuscleGroup",
    item_db_id: payload.id,
    item_db_name: payload.name,
    defaultItem: payload.defaultItem,
    user_id:payload.user_id
  }
  )
);

export const deleteMuscleGroup = createAsyncThunk(
  "deleteMuscleGroup",
  async (payload) => await envAwarePost("api/events/api/", {
    action: "deleteMuscleGroup",
    item_db_id: payload.id,
    defaultItem: payload.defaultItem,
    user_id:payload.user_id
  }
  )
);

export const addAllFitswDefaults = createAsyncThunk(
  'addAllFitswDefaults',
  async () => await envAwarePost('api/exercises/api/', {
    action: 'addAllFitswDefaults',
  })
)


export const deleteAllExercises = createAsyncThunk(
  'deleteAllExercises',
  async () => await envAwarePost('api/exercises/api/', {
    action: 'deleteAllExercises',
  })
)

export const deleteMultipleExercises = createAsyncThunk(
  'deleteMultipleExercises',
  async (payload) => await envAwarePost('api/exercises/api/', {
    action: 'deleteMultipleExercises', ...payload
  })
)

export const deleteExercise = createAsyncThunk(
  'deleteExercise',
  async payload => await envAwarePost('api/exercises/api/', {
    ...payload
  })
)

export const addExercise = createAsyncThunk(
  'addExercise',
  async payload => envAwareFormSubmit({
    path: 'api/exercises/api/',
    payload
  })
)

export const editExercise = createAsyncThunk(
  'editExercise',
  async payload => envAwareFormSubmit({
    path: 'api/exercises/api/',
    payload
  })
)


export const copyExercise = createAsyncThunk(
  'copyExercise',
  async payload => await envAwarePost('api/exercises/api/', {
    ...payload
  })
)

export const getNumRemainingDemoVideos = createAsyncThunk(
  'getNumRemainingDemoVideos', 
  async () => await envAwarePost('api/exercises/api/',
    { action: "getNumRemainingDemoVideos" }
  )
)

const musclesAdapter = createEntityAdapter({
  selectId: ent => ent.muscle_id,
  status: 'idle',
})

const libraryAdapter = createEntityAdapter({
  selectId: ent => ent.exercise_db_id,
  status: 'init',
  tgtUser: null,
})

const exerciseSlice = createSlice({
  name: "exerciseSlice",
  initialState: {
    exerciseList: [],
    additionalExerciseData: {},
    exerciseListStatus: "idle",
    addingExerciseStatus: "idle",
    muscles: musclesAdapter.getInitialState(),
    library: libraryAdapter.getInitialState()
  },
  reducers: {
    addMany:(state,action)=>{
      libraryAdapter.upsertMany(state.library,action.payload)
    }
  },
  extraReducers: {
    [removeMuscleGroups.pending] : (state,action) => {
      const muscle_ids = action.meta.arg
      musclesAdapter.removeMany(state.muscles,muscle_ids)
    },
    [getLibraryExercises.pending] : (state,action) => {
      const {tgt_user_id_number:tgtUser} = action.meta.arg
      state.library.status = 'pending'
      state.library.tgtUser = tgtUser
    },
    [getLibraryExercises.fulfilled] : (state,action) => {
      state.library.status = 'idle'
      const {success,data:{exercises}} = action.payload
      if( success === '1' ){
        libraryAdapter.setAll(state.library,exercises)
      }
    },
    [getExerciseList.pending]: (state, action) => {
      state.exerciseListStatus = "fetching";
    },
    [getExerciseList.fulfilled]: (state, action) => {
      const { success, data } = action.payload;
      if (success == 1) {
        state.exerciseList = data;
        state.exerciseListStatus = "idle";
      }
    },
    [getLibraryExercisesAfterAdding.fulfilled]: (state, action) => {
      const {success,data:{exercises}} = action.payload
        if( success === '1' ){
          libraryAdapter.upsertMany(state.library,exercises)
        }
    },
    [getAdditionalExerciseData.fulfilled]: (state, action) => {
      const { success, data } = action.payload;
      if (success == 1) {
        state.additionalExerciseData = data;
      }
    },
    [addExercise.pending]: (state, action) => {
      state.addingExerciseStatus = "fetching"
    },
    [addExercise.fulfilled]: (state, action) => {
      const {success,data} = action.payload
      state.addingExerciseStatus = "idle"
      if(success === '1'){
        libraryAdapter.upsertOne(state.library,data.entity)
      }
    },
    [editExercise.fulfilled]: (state, action) => {
      const {success,data:{exercise}} = action.payload
      let sendId = action.meta.arg.id;
      let id = exercise.exercise_db_id
      let updateObject = {id, changes: action.payload.data.exercise};

      //id returned will be different then sent if its a default exercise being updated
      //since default is hidden and new exercise created
      if( success === '1'){
        if (sendId === id){
          libraryAdapter.updateOne(state.library,updateObject);
        } else {
          libraryAdapter.removeOne(state.library,sendId);
          libraryAdapter.upsertOne(state.library,action.payload.data.exercise);
        }
      } 
    },
    [deleteExercise.pending]: (state, action) => {
      //optimistically remove item from state & UI
      const { id } = action.meta.arg
      libraryAdapter.removeOne(state.library,id) 
    },
    [deleteMultipleExercises.pending]: (state, action) => {
      //optimistically remove item from state & UI
      const { ids } = action.meta.arg
      libraryAdapter.removeMany(state.library,ids) 
    },
    [deleteAllExercises.pending]: (state, action) => {
      //optimistically remove item from state & UI  
        libraryAdapter.setAll(state.library,[])  
    },    

    [getMuscleGroups.pending]: (state, action) => {
      state.exerciseListStatus = "fetching";
    },
    [getMuscleGroups.fulfilled]: (state, action) => {
      const { success, data } = action.payload;
      // console.log('DATA: ', data)
      if (success == 1) {
        state.exerciseListStatus = "idle";
        musclesAdapter.setAll(state.muscles, data)
      }
    },
    [editMuscleGroup.fulfilled]: (state, action) => {
      const { success, data } = action.payload;
      if (success == 1) {
        const updateObject = {
          id: action.payload.item_db_id,
          changes: {
            muscleGroup: action.payload.item_db_name,
          },
        }
        musclesAdapter.updateOne(state.muscles, updateObject)
      }
    },
    [deleteMuscleGroup.fulfilled]: (state, action) => {
      const { success, data } = action.payload;
      if (success == 1) {
        let id = action.meta.arg.id
        musclesAdapter.removeOne(state.muscles, id)
      }
    },
  },
});

export const {addMany} = exerciseSlice.actions

export const selectLibrary = state => state.exercise.library

// Export the customized selectors for this adapter using `getSelectors`
export const {
  selectAll: selectAllLibraryExercises,
  selectById: selectLibraryExerciseById,
  selectIds: selectLibraryExerciseIds
  // Pass in a selector that returns the posts slice of state
} = libraryAdapter.getSelectors(state => state.exercise.library)

export default exerciseSlice.reducer;